/** Mouse firmware catalog helpers (shared with main-app device card probe) */

export type MouseFwItem = {
  type?: string;
  version: string;
  fileName?: string;
  toolFileName?: string;
  toolDownloadName?: string;
  releaseNotes?: string | string[];
  releaseDate?: string;
  size?: number | string | null;
};

export type MouseFirmwareLinkType = "wired" | "wireless1k" | "wireless8k";
export type MouseDongleType = "dongle1k" | "dongle8k";

export type MouseFirmwareTarget = {
  version: string;
  releaseNotes?: string[];
  size?: number | string | null;
};

import { getFirmwareFileUrl } from "./firmwareFileUrl";

/** 兼容 `"说明"` / `["说明1","说明2"]` */
function normalizeReleaseNotes(raw: unknown): string[] {
  if (raw == null) return [];
  if (typeof raw === "string") {
    const s = raw.trim();
    return s ? [s] : [];
  }
  if (Array.isArray(raw)) {
    return raw.map((n) => String(n ?? "").trim()).filter(Boolean);
  }
  return [];
}
/** 去掉前缀后的版本核心串，用于判断占位/无效（如 0000、0.0.0） */
export function fwVerCore(s: string): string {
  return String(s)
    .trim()
    .replace(/^[Vv][.\s]*/, "")
    .trim();
}

export function isPlaceholderFwVer(raw: unknown): boolean {
  if (raw == null) return true;
  const s = String(raw).trim();
  if (!s) return true;
  const core = fwVerCore(s);
  if (!core) return true;
  const parts = core
    .split(".")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return true;
  return parts.every((p) => {
    if (!/^\d+$/.test(p)) return false;
    return /^0+$/.test(p) || Number(p) === 0;
  });
}

/** 写入 state 用：无效版本归一成 ""，否则大写保留原格式 */
export function normFwVer(raw: unknown): string {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || isPlaceholderFwVer(s)) return "";
  return s.toUpperCase();
}

export function hasNormFwVer(raw: unknown): boolean {
  return normFwVer(raw) !== "";
}

export function isFwItem(f: unknown): f is MouseFwItem {
  return (
    typeof f === "object" &&
    f !== null &&
    "version" in f &&
    typeof (f as MouseFwItem).version === "string"
  );
}

/** 将 versions.json 规范为一维固件对象（兼容误写的嵌套数组） */
export function normalizeFwJsonList(raw: unknown): unknown[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) =>
    Array.isArray(item) ? item : item != null ? [item] : []
  );
}

export function fwByType(list: unknown[], type: string): MouseFwItem | null {
  const hit = list.find((f) => isFwItem(f) && f.type === type) as
    | MouseFwItem
    | undefined;
  return hit ?? null;
}

/** 线上下载包名：优先 fileName（匠盟 / 展立 / 富芮坤等），兼容 toolFileName（贝盈） */
export function resolveFwBinFileName(item: MouseFwItem | null): string | null {
  if (!item) return null;
  const name = item.fileName?.trim() || item.toolFileName?.trim();
  return name || null;
}

/**
 * Alpha7 / 展立版本分值：
 * - 新格式 v1.7.6 / V1.7.6 / 1.7.6 → major*10000 + minor*100 + patch（避免 1.10.0 与 2.0.0 碰撞）
 * - 旧格式 V155 / 155 → 纯数字分值（兼容历史目录）
 */
function parseAlpha7FirmwareScore(version: string): number | null {
  const core = fwVerCore(version);
  if (/^\d+$/.test(core)) return Number(core);
  const dotted = /^(\d+)\.(\d+)\.(\d+)$/.exec(core);
  if (dotted) {
    return (
      Number(dotted[1]) * 10000 + Number(dotted[2]) * 100 + Number(dotted[3])
    );
  }
  // 兼容仅两段的 v1.7
  const two = /^(\d+)\.(\d+)$/.exec(core);
  if (two) {
    return Number(two[1]) * 10000 + Number(two[2]) * 100;
  }
  return null;
}

function isZhanliFirmwareDir(resolvedDir: string | null): boolean {
  if (!resolvedDir) return false;
  const base =
    resolvedDir.replace(/\\/g, "/").replace(/\/+$/, "").split("/").pop() ?? "";
  return (
    base === "z7max" ||
    base === "z7max-2.4G" ||
    base === "alpha7" ||
    base === "alpha7-2.4G"
  );
}

export function pickFw(
  list: unknown[],
  preferredDongleType: MouseDongleType | null,
  resolvedDir: string | null = null
) {
  const mouseHistory = listFwByTypes(list, ["mouse", "usb"], resolvedDir);
  const preferredDongle = preferredDongleType
    ? fwByType(list, preferredDongleType)
    : null;
  const fallbackDongle =
    fwByType(list, "2.4g") ||
    fwByType(list, "2.4G") ||
    fwByType(list, "dongle") ||
    fwByType(list, "dongle1k") ||
    fwByType(list, "dongle8k");
  return {
    mouse: mouseHistory[0] ?? null,
    mouseHistory,
    dongle: preferredDongle || fallbackDongle,
  };
}

export const ZHANLI_DONGLE_FW_TYPES = [
  "2.4g",
  "2.4G",
  "dongle",
  "dongle1k",
  "dongle8k",
];

export function dongleTypeFromLink(
  link: MouseFirmwareLinkType | null
): MouseDongleType | null {
  if (link === "wireless1k") return "dongle1k";
  if (link === "wireless8k") return "dongle8k";
  return null;
}

function parseS9UltimateVersion(v: string): number | null {
  const core = fwVerCore(v);
  if (/^\d{4}$/.test(core)) return Number(core);
  const m = /^(\d+)\.(\d+)$/.exec(core);
  if (!m) return null;
  return Number(m[1]) * 100 + Number(m[2]);
}

export function cmpFirmwareVersion(
  v1: string,
  v2: string,
  resolvedDir: string | null
): number {
  if (resolvedDir === "S9Ultimate") {
    const s1 = parseS9UltimateVersion(v1);
    const s2 = parseS9UltimateVersion(v2);
    if (s1 != null && s2 != null) return s1 - s2;
  }
  if (isZhanliFirmwareDir(resolvedDir)) {
    const s1 = parseAlpha7FirmwareScore(v1);
    const s2 = parseAlpha7FirmwareScore(v2);
    if (s1 != null && s2 != null) return s1 - s2;
  }
  // 通用：去掉 V/v 前缀后按段数值比较（兼容 v1.7.6 与 1.7.6）
  const parts1 = fwVerCore(v1)
    .split(".")
    .map((p) => Number(p))
    .map((n) => (Number.isFinite(n) ? n : 0));
  const parts2 = fwVerCore(v2)
    .split(".")
    .map((p) => Number(p))
    .map((n) => (Number.isFinite(n) ? n : 0));
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 !== p2) return p1 - p2;
  }
  return 0;
}

/** 按类型收集并按版本降序（最新在前） */
export function listFwByTypes(
  list: unknown[],
  types: string[],
  resolvedDir: string | null
): MouseFwItem[] {
  const typeSet = new Set(types);
  const items = list.filter(
    (f): f is MouseFwItem => isFwItem(f) && typeSet.has(String(f.type || ""))
  );
  return [...items].sort((a, b) =>
    cmpFirmwareVersion(b.version, a.version, resolvedDir)
  );
}

export function withVersionPrefix(version?: string): string {
  if (!version || isPlaceholderFwVer(version)) return "";
  const normalized = fwVerCore(version);
  return normalized ? `V.${normalized}` : "";
}

export function downloadBlob(blob: Blob, downloadName: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = downloadName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function isDongleFwItem(item: MouseFwItem | null): boolean {
  if (!item) return false;
  const type = String(item.type || "").toLowerCase();
  return (
    type === "dongle" ||
    type === "dongle1k" ||
    type === "dongle8k" ||
    type === "2.4g"
  );
}

export function toTargetFirmware(
  item: MouseFwItem | null,
  withPrefix = withVersionPrefix
): MouseFirmwareTarget | null {
  if (!item) return null;
  const releaseNotes = normalizeReleaseNotes(item.releaseNotes);
  return {
    version: withPrefix(item.version) || item.version,
    releaseNotes: releaseNotes.length > 0 ? releaseNotes : undefined,
    size: item.size,
  };
}

export type MouseFirmwareUpdateEval = {
  hasUpdate: boolean;
  offerVersion: string | null;
};

export function evaluateMouseFirmwareUpdate(args: {
  provider: string;
  resolvedDir: string;
  resolvedDongleDir: string | null;
  resolvedLink: MouseFirmwareLinkType | null;
  isWired?: boolean;
  mVer: string;
  dVer: string;
  latM: MouseFwItem | null;
  latD: MouseFwItem | null;
}): MouseFirmwareUpdateEval {
  const {
    provider,
    resolvedDir,
    resolvedDongleDir,
    resolvedLink,
    isWired,
    mVer,
    dVer,
    latM,
    latD,
  } = args;
  const isZhanli = provider === "zhanli";
  const usesRuntimeWiredLink =
    provider === "jiangmeng" || provider === "beiying" || provider === "zhanli";
  const isWiredMode = usesRuntimeWiredLink
    ? isWired === true
    : resolvedLink === "wired";

  const mouseNeedsUpdate = !!(
    mVer &&
    latM &&
    cmpFirmwareVersion(latM.version, mVer, resolvedDir) > 0
  );
  const dongleNeedsUpdate = isZhanli
    ? !!(
        dVer &&
        latM &&
        cmpFirmwareVersion(latM.version, dVer, resolvedDir) > 0
      )
    : !!(
        dVer &&
        latD &&
        cmpFirmwareVersion(
          latD.version,
          dVer,
          resolvedDongleDir ?? resolvedDir
        ) > 0
      );
  const hasUpdate = isWiredMode
    ? mouseNeedsUpdate
    : mouseNeedsUpdate || dongleNeedsUpdate;

  let offerVersion: string | null = null;
  if (mouseNeedsUpdate && latM?.version) {
    offerVersion = latM.version;
  } else if (dongleNeedsUpdate) {
    if (isZhanli && latM?.version) offerVersion = latM.version;
    else if (latD?.version) offerVersion = latD.version;
  }

  return { hasUpdate, offerVersion };
}

export function computeMouseHasFirmwareUpdate(
  args: Parameters<typeof evaluateMouseFirmwareUpdate>[0]
): boolean {
  return evaluateMouseFirmwareUpdate(args).hasUpdate;
}

const mouseCatalogPickCache = new Map<
  string,
  { latM: MouseFwItem | null; latD: MouseFwItem | null; at: number }
>();
const MOUSE_CATALOG_CACHE_MS = 5 * 60 * 1000;

export async function loadMouseFirmwareCatalogPick(
  dir: string,
  link: MouseFirmwareLinkType | null,
  extraDongleDir: string | null,
  provider: string
): Promise<{ latM: MouseFwItem | null; latD: MouseFwItem | null }> {
  const cacheKey = `${provider}|${dir}|${link ?? ""}|${extraDongleDir ?? ""}`;
  const cached = mouseCatalogPickCache.get(cacheKey);
  if (cached && Date.now() - cached.at < MOUSE_CATALOG_CACHE_MS) {
    return { latM: cached.latM, latD: cached.latD };
  }
  const res = await fetch(getFirmwareFileUrl(dir, "versions.json"));
  if (!res.ok) return { latM: null, latD: null };
  const list = normalizeFwJsonList(await res.json());
  const preferredDongleType = dongleTypeFromLink(link);
  const { mouse, dongle } = pickFw(list, preferredDongleType, dir);
  let latD = dongle;
  const extra = provider === "zhanli" ? null : extraDongleDir?.trim() || null;
  if (extra && extra !== dir) {
    try {
      const dongleRes = await fetch(getFirmwareFileUrl(extra, "versions.json"));
      if (dongleRes.ok) {
        const dongleList = normalizeFwJsonList(await dongleRes.json());
        const dongleItems = listFwByTypes(
          dongleList,
          ZHANLI_DONGLE_FW_TYPES,
          extra
        );
        latD = dongleItems[0] ?? dongle;
      }
    } catch {
      /* keep latD from primary list */
    }
  }
  const result = { latM: mouse, latD };
  mouseCatalogPickCache.set(cacheKey, { ...result, at: Date.now() });
  return result;
}
